/**
 * Memoria_Operacional de la Capa Operacional AI-First.
 *
 * Implementa la interfaz {@link Memoria} de `../api/contracts` para la
 * variante **demo** (client-side, R17.1/R17.5). La Memoria_Operacional
 * persiste **decisiones humanas**, **recomendaciones de IA** (con su
 * estado de respuesta) y **outcomes** reales, preservando el enlace
 * `recomendación · decisión · outcome` para que la tasa de aceptación y
 * la tasa de acierto sean computables de forma **determinista** en el
 * rango `[0, 1]` por entidad y por tipo (R10).
 *
 * Persistencia con **degradación elegante**, en el mismo espíritu que
 * `eventStore.ts`:
 *
 *   1. **IndexedDB** (base propia `anc-memoria`, object stores con
 *      `keyPath="id"` y `put()` idempotente) cuando está disponible;
 *   2. **localStorage** (un mapa JSON `id → registro` por colección)
 *      como primer fallback persistente;
 *   3. **memoria en proceso** (Node/Vitest/jsdom sin almacenamiento).
 *
 * Reglas de negocio aplicadas (R10):
 *
 *   - `recordDecision(d)` persiste la decisión **verbatim**, preservando
 *     autor, timestamp y cualquier metadato de contexto/entidad asociado
 *     (R10.1, Property 27).
 *   - `recordRecommendation(r, estado)` valida que `estado ∈ {aceptada,
 *     rechazada, pendiente}` y persiste la recomendación con su estado
 *     (R10.2, Property 27).
 *   - `recordOutcome(o, origen_ref)` **rechaza** con error todo outcome
 *     cuyo origen no enlace a una recomendación o decisión existente
 *     (R10.6); en caso válido preserva el enlace (R10.3, R10.5).
 *   - `rates(entidad, tipo)` calcula de forma determinista
 *     `{ aceptacion, acierto } ∈ [0, 1]` (R10.5).
 *
 * El registro es **idempotente por identificador**: registrar dos veces
 * el mismo `decision_id` / `recomendacion_id` / `outcome_id` no duplica
 * el conteo (gana el último, vía `put`), de modo que las tasas son
 * estables ante reintentos (Property 26).
 *
 * Las funciones puras {@link computarTasas}, {@link esAcierto} y
 * {@link coincideFiltro} se exportan para que las pruebas PBT
 * (Properties 26, 27) las ejerciten sin almacenamiento real.
 */

import type {
  Decision,
  EntidadRef,
  EstadoRecomendacion,
  Id,
  Memoria,
  Outcome,
  Recomendacion,
  Result,
  TasasMemoria,
  IsoTimestamp,
  Ventana,
} from "../api/contracts";

// ---------------------------------------------------------------------------
// Constantes de dominio
// ---------------------------------------------------------------------------

/** Conjunto cerrado de estados de respuesta de una recomendación (R10.2, Property 27). */
export const ESTADOS_RECOMENDACION: ReadonlySet<EstadoRecomendacion> = new Set<EstadoRecomendacion>([
  "aceptada",
  "rechazada",
  "pendiente",
]);

/**
 * Conjunto (normalizado a minúsculas) de `resultado` de un Outcome que se
 * interpreta como **acierto** cuando el Outcome no trae una bandera
 * `acierto` explícita. Convención determinista del demo.
 */
const RESULTADOS_ACIERTO: ReadonlySet<string> = new Set<string>([
  "confirmado",
  "confirmada",
  "acierto",
  "exito",
  "éxito",
  "positivo",
  "verdadero_positivo",
  "fuga_confirmada",
  "volumen_recuperado",
  "true",
]);

/** Colecciones persistidas de la Memoria_Operacional. */
const COLECCIONES = ["decisiones", "recomendaciones", "outcomes"] as const;
type Coleccion = (typeof COLECCIONES)[number];

const DB_NAME = "anc-memoria";
const DB_VERSION = 1;
const LS_PREFIX = "anc.memoria.";

// ---------------------------------------------------------------------------
// Modelos internos
// ---------------------------------------------------------------------------

/** Asociación opcional de un registro a una entidad y tipo, para filtrar tasas. */
export interface AsociacionMemoria {
  entidad?: EntidadRef;
  tipo?: string;
}

/** Wrapper persistido: identifica el registro y guarda el valor verbatim. */
interface RegistroMemoria<T> {
  id: Id;
  value: T;
}

function mensajeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------------------
// Lectura best-effort de metadatos (entidad/tipo/acierto) de objetos del dominio
// ---------------------------------------------------------------------------

function esEntidadRef(v: unknown): v is EntidadRef {
  if (v === null || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return typeof r.tipo === "string" && typeof r.id === "string";
}

/**
 * Extrae la {@link AsociacionMemoria} de un objeto del dominio leyendo
 * campos opcionales (`entidad`, `tipo`/`tipo_recomendacion`). Los tipos
 * del contrato (`Decision`, `Recomendacion`, `Outcome`) son mínimos, pero
 * los objetos del demo pueden adjuntar estos metadatos para habilitar el
 * desglose de tasas por entidad y por tipo (R10.5).
 */
export function leerAsociacion(obj: unknown): AsociacionMemoria {
  if (obj === null || typeof obj !== "object") return {};
  const r = obj as Record<string, unknown>;
  const entidad = esEntidadRef(r.entidad) ? (r.entidad as EntidadRef) : undefined;
  let tipo: string | undefined;
  if (typeof r.tipo === "string") tipo = r.tipo;
  else if (typeof r.tipo_recomendacion === "string") tipo = r.tipo_recomendacion;
  return { entidad, tipo };
}

/**
 * Determina si un Outcome es un **acierto**: prioriza una bandera
 * booleana `acierto` si el objeto la trae; en su defecto, clasifica por
 * el string `resultado` contra {@link RESULTADOS_ACIERTO}. Determinista.
 */
export function esAcierto(o: Outcome): boolean {
  const r = o as unknown as Record<string, unknown>;
  if (typeof r.acierto === "boolean") return r.acierto;
  const res = (o.resultado ?? "").toString().trim().toLowerCase();
  return RESULTADOS_ACIERTO.has(res);
}

/**
 * Decide si un registro con asociación `assoc` cae dentro del filtro
 * `(entidad, tipo)`. Un registro **sin** asociación se considera
 * aplicable a cualquier consulta (contribuye al cálculo global); un
 * registro con asociación solo coincide si la entidad y/o el tipo
 * declarados igualan a los del filtro. `tipo` vacío actúa como comodín.
 */
export function coincideFiltro(
  assoc: AsociacionMemoria,
  entidad: EntidadRef,
  tipo: string,
): boolean {
  if (assoc.entidad && (assoc.entidad.id !== entidad.id || assoc.entidad.tipo !== entidad.tipo)) {
    return false;
  }
  if (assoc.tipo && tipo.length > 0 && assoc.tipo !== tipo) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// computarTasas — cálculo puro y determinista de tasas (R10.5)
// ---------------------------------------------------------------------------

/**
 * Calcula de forma **determinista** las tasas de la Memoria_Operacional
 * para `(entidad, tipo)` a partir de las colecciones materializadas
 * (R10.5):
 *
 *   - **aceptacion** = recomendaciones `aceptada` ÷ recomendaciones
 *     resueltas (`aceptada` + `rechazada`); 0 si no hay resueltas.
 *   - **acierto** = outcomes acierto ÷ outcomes totales; 0 si no hay
 *     outcomes.
 *
 * Ambas quedan acotadas a `[0, 1]` por construcción. El origen de cada
 * outcome se resuelve sobre las recomendaciones/decisiones para heredar
 * su asociación cuando el propio outcome no la declara.
 */
export function computarTasas(
  recomendaciones: ReadonlyArray<{ rec: Recomendacion; estado: EstadoRecomendacion }>,
  decisiones: ReadonlyArray<Decision>,
  outcomes: ReadonlyArray<{ outcome: Outcome; origen_ref: EntidadRef }>,
  entidad: EntidadRef,
  tipo: string,
): TasasMemoria {
  // Índice de asociación por id de recomendación/decisión para heredar al outcome.
  const assocPorId = new Map<Id, AsociacionMemoria>();
  for (const { rec } of recomendaciones) assocPorId.set(rec.recomendacion_id, leerAsociacion(rec));
  for (const d of decisiones) assocPorId.set(d.decision_id, leerAsociacion(d));

  // Tasa de aceptación: aceptadas / (aceptadas + rechazadas) en el filtro.
  let aceptadas = 0;
  let resueltas = 0;
  for (const { rec, estado } of recomendaciones) {
    if (!coincideFiltro(leerAsociacion(rec), entidad, tipo)) continue;
    if (estado === "aceptada") {
      aceptadas += 1;
      resueltas += 1;
    } else if (estado === "rechazada") {
      resueltas += 1;
    }
  }
  const aceptacion = resueltas > 0 ? aceptadas / resueltas : 0;

  // Tasa de acierto: outcomes acierto / outcomes totales en el filtro.
  let aciertos = 0;
  let totalOutcomes = 0;
  for (const { outcome, origen_ref } of outcomes) {
    const assoc = assocPorId.get(origen_ref.id) ?? leerAsociacion(outcome);
    if (!coincideFiltro(assoc, entidad, tipo)) continue;
    totalOutcomes += 1;
    if (esAcierto(outcome)) aciertos += 1;
  }
  const acierto = totalOutcomes > 0 ? aciertos / totalOutcomes : 0;

  return { aceptacion, acierto };
}

// ---------------------------------------------------------------------------
// Capa de persistencia con degradación elegante (IndexedDB → localStorage → memoria)
// ---------------------------------------------------------------------------

/** Almacén clave-valor por colección, idempotente por `id` (upsert). */
interface MemoriaStore {
  /** Respaldo activo: útil para diagnóstico/test. */
  readonly kind: "indexeddb" | "localstorage" | "memory";
  put<T>(coleccion: Coleccion, registro: RegistroMemoria<T>): Promise<void>;
  getAll<T>(coleccion: Coleccion): Promise<Array<RegistroMemoria<T>>>;
  clear(): Promise<void>;
}

function indexedDbAvailable(): boolean {
  try {
    return (
      typeof globalThis !== "undefined" &&
      typeof globalThis.indexedDB !== "undefined" &&
      globalThis.indexedDB !== null
    );
  } catch {
    return false;
  }
}

function localStorageAvailable(): boolean {
  try {
    if (typeof globalThis === "undefined") return false;
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    if (!ls) return false;
    const probe = `${LS_PREFIX}__probe__`;
    ls.setItem(probe, "1");
    ls.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

/** Implementación en memoria (fallback final). */
class InMemoryMemoriaStore implements MemoriaStore {
  readonly kind = "memory" as const;
  private readonly mapas: Record<Coleccion, Map<Id, RegistroMemoria<unknown>>> = {
    decisiones: new Map(),
    recomendaciones: new Map(),
    outcomes: new Map(),
  };

  async put<T>(coleccion: Coleccion, registro: RegistroMemoria<T>): Promise<void> {
    this.mapas[coleccion].set(registro.id, registro as RegistroMemoria<unknown>);
  }

  async getAll<T>(coleccion: Coleccion): Promise<Array<RegistroMemoria<T>>> {
    return [...this.mapas[coleccion].values()].map((r) => ({ ...r })) as Array<RegistroMemoria<T>>;
  }

  async clear(): Promise<void> {
    for (const c of COLECCIONES) this.mapas[c].clear();
  }
}

/** Implementación sobre `localStorage` (un mapa JSON por colección). */
class LocalStorageMemoriaStore implements MemoriaStore {
  readonly kind = "localstorage" as const;
  private readonly ls: Storage;

  constructor(ls: Storage) {
    this.ls = ls;
  }

  private leerMapa<T>(coleccion: Coleccion): Record<Id, RegistroMemoria<T>> {
    const raw = this.ls.getItem(LS_PREFIX + coleccion);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? (parsed as Record<Id, RegistroMemoria<T>>) : {};
    } catch {
      return {};
    }
  }

  async put<T>(coleccion: Coleccion, registro: RegistroMemoria<T>): Promise<void> {
    const mapa = this.leerMapa<T>(coleccion);
    mapa[registro.id] = registro;
    this.ls.setItem(LS_PREFIX + coleccion, JSON.stringify(mapa));
  }

  async getAll<T>(coleccion: Coleccion): Promise<Array<RegistroMemoria<T>>> {
    return Object.values(this.leerMapa<T>(coleccion));
  }

  async clear(): Promise<void> {
    for (const c of COLECCIONES) this.ls.removeItem(LS_PREFIX + c);
  }
}

/** Implementación sobre IndexedDB (object stores con `keyPath="id"`). */
class IndexedDbMemoriaStore implements MemoriaStore {
  readonly kind = "indexeddb" as const;

  constructor(private readonly db: IDBDatabase) {}

  async put<T>(coleccion: Coleccion, registro: RegistroMemoria<T>): Promise<void> {
    const store = this.db.transaction(coleccion, "readwrite").objectStore(coleccion);
    await promisifyRequest(store.put(registro));
  }

  async getAll<T>(coleccion: Coleccion): Promise<Array<RegistroMemoria<T>>> {
    const store = this.db.transaction(coleccion, "readonly").objectStore(coleccion);
    return (await promisifyRequest(store.getAll())) as Array<RegistroMemoria<T>>;
  }

  async clear(): Promise<void> {
    for (const c of COLECCIONES) {
      await promisifyRequest(this.db.transaction(c, "readwrite").objectStore(c).clear());
    }
  }
}

/**
 * Abre el {@link MemoriaStore} con la mejor opción disponible:
 * IndexedDB → localStorage → memoria. Nunca lanza: ante cualquier fallo
 * conmuta al siguiente respaldo (degradación elegante, R17.6).
 */
export async function openMemoriaStore(): Promise<MemoriaStore> {
  if (indexedDbAvailable()) {
    try {
      const db = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
          const database = request.result;
          for (const c of COLECCIONES) {
            if (!database.objectStoreNames.contains(c)) {
              database.createObjectStore(c, { keyPath: "id" });
            }
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("No se pudo abrir IndexedDB"));
      });
      const completo = COLECCIONES.every((c) => db.objectStoreNames.contains(c));
      if (completo) return new IndexedDbMemoriaStore(db);
      db.close();
    } catch {
      /* fallback abajo */
    }
  }
  if (localStorageAvailable()) {
    return new LocalStorageMemoriaStore((globalThis as { localStorage: Storage }).localStorage);
  }
  return new InMemoryMemoriaStore();
}

// ---------------------------------------------------------------------------
// MemoriaOperacionalLocal — implementación demo de la interfaz Memoria
// ---------------------------------------------------------------------------

/** Tipo del valor persistido en la colección de recomendaciones. */
interface RecomendacionPersistida {
  rec: Recomendacion;
  estado: EstadoRecomendacion;
}

/** Tipo del valor persistido en la colección de outcomes. */
interface OutcomePersistido {
  outcome: Outcome;
  origen_ref: EntidadRef;
}

/**
 * Enlaces recomendación·decisión·outcome materializados que la
 * AI_Observability (tarea 10.2) consume para derivar la AI acceptance
 * rate, la tasa de falsos positivos y la tasa de reversión (R14.5,
 * R20.4). Las colecciones se entregan **verbatim** (con su estado y su
 * enlace de origen) para que el cálculo de métricas viva en la
 * Observabilidad, no en la Memoria.
 */
export interface EnlacesMemoria {
  recomendaciones: Array<{ rec: Recomendacion; estado: EstadoRecomendacion }>;
  decisiones: Decision[];
  outcomes: Array<{ outcome: Outcome; origen_ref: EntidadRef }>;
}

/**
 * Capacidad de **lectura mínima** de los enlaces de la Memoria, separada
 * de la interfaz {@link Memoria} del contrato para no alterarlo. La
 * AI_Observability narrowea el servicio `runtime.memoria()` a esta
 * capacidad (duck-typing) cuando está disponible; si no lo está, degrada
 * con elegancia a las muestras de métrica registradas (R17.6).
 */
export interface MemoriaLectura {
  /** Devuelve los enlaces rec·dec·outcome, opcionalmente acotados a `ventana`. */
  enlaces(ventana?: Ventana): Promise<Result<EnlacesMemoria>>;
}

/** ¿Es `obj` una {@link MemoriaLectura} (expone `enlaces`)? */
export function tieneLecturaEnlaces(obj: unknown): obj is MemoriaLectura {
  return (
    obj !== null &&
    typeof obj === "object" &&
    typeof (obj as { enlaces?: unknown }).enlaces === "function"
  );
}

/** ¿Cae `ts` dentro de `ventana` (inclusive)? Sin ventana → siempre true. */
function tsEnVentana(ts: IsoTimestamp | undefined, ventana?: Ventana): boolean {
  if (!ventana) return true;
  const t = Date.parse(ts ?? "");
  const desde = Date.parse(ventana.desde);
  const hasta = Date.parse(ventana.hasta);
  if (!Number.isFinite(t) || !Number.isFinite(desde) || !Number.isFinite(hasta)) return false;
  if (desde > hasta) return false;
  return t >= desde && t <= hasta;
}

/** Opciones de construcción de {@link MemoriaOperacionalLocal}. */
export interface MemoriaLocalOptions {
  /** Almacén a usar (inyectable en tests). Por defecto IndexedDB/localStorage/memoria. */
  store?: MemoriaStore;
}

/**
 * Memoria_Operacional client-side que implementa {@link Memoria}.
 *
 * Mantiene una caché en memoria deduplicada por id (idempotencia,
 * Property 26) sincronizada con el almacén persistente subyacente.
 */
export class MemoriaOperacionalLocal implements Memoria, MemoriaLectura {
  private store: MemoriaStore | null;
  private cargado = false;

  private readonly decisiones = new Map<Id, Decision>();
  private readonly recomendaciones = new Map<Id, RecomendacionPersistida>();
  private readonly outcomes = new Map<Id, OutcomePersistido>();

  constructor(opts?: MemoriaLocalOptions) {
    this.store = opts?.store ?? null;
  }

  /** Abre el almacén (perezoso) y carga las colecciones en la caché. */
  private async asegurar(): Promise<MemoriaStore> {
    if (!this.store) this.store = await openMemoriaStore();
    if (!this.cargado) {
      for (const r of await this.store.getAll<Decision>("decisiones")) {
        this.decisiones.set(r.id, r.value);
      }
      for (const r of await this.store.getAll<RecomendacionPersistida>("recomendaciones")) {
        this.recomendaciones.set(r.id, r.value);
      }
      for (const r of await this.store.getAll<OutcomePersistido>("outcomes")) {
        this.outcomes.set(r.id, r.value);
      }
      this.cargado = true;
    }
    return this.store;
  }

  /**
   * Persiste una decisión humana verbatim, preservando autor, timestamp y
   * cualquier metadato de contexto/entidad asociado (R10.1, Property 27).
   */
  async recordDecision(d: Decision): Promise<Result<void>> {
    try {
      if (!d || typeof d.decision_id !== "string" || d.decision_id.length === 0) {
        return { ok: false, error: "La decisión carece de un decision_id válido.", degraded: false };
      }
      if (typeof d.actor !== "string" || d.actor.length === 0) {
        return { ok: false, error: "La decisión debe registrar su autor (actor) (R10.1).", degraded: false };
      }
      if (typeof d.ts !== "string" || d.ts.length === 0) {
        return { ok: false, error: "La decisión debe registrar su timestamp (ts) (R10.1).", degraded: false };
      }
      const store = await this.asegurar();
      this.decisiones.set(d.decision_id, d);
      await store.put<Decision>("decisiones", { id: d.decision_id, value: d });
      return { ok: true, value: undefined };
    } catch (err) {
      return { ok: false, error: `No se pudo registrar la decisión: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Persiste una recomendación de IA con su estado de respuesta. Valida
   * que el estado pertenezca al conjunto cerrado `{aceptada, rechazada,
   * pendiente}` (R10.2, Property 27).
   */
  async recordRecommendation(r: Recomendacion, estado: EstadoRecomendacion): Promise<Result<void>> {
    try {
      if (!r || typeof r.recomendacion_id !== "string" || r.recomendacion_id.length === 0) {
        return { ok: false, error: "La recomendación carece de un recomendacion_id válido.", degraded: false };
      }
      if (!ESTADOS_RECOMENDACION.has(estado)) {
        return {
          ok: false,
          error: `Estado de recomendación inválido '${String(estado)}'; debe ser uno de {aceptada, rechazada, pendiente} (R10.2).`,
          degraded: false,
        };
      }
      const store = await this.asegurar();
      // Normaliza el estado dentro del propio objeto para consistencia.
      const persistido: RecomendacionPersistida = { rec: { ...r, estado }, estado };
      this.recomendaciones.set(r.recomendacion_id, persistido);
      await store.put<RecomendacionPersistida>("recomendaciones", {
        id: r.recomendacion_id,
        value: persistido,
      });
      return { ok: true, value: undefined };
    } catch (err) {
      return { ok: false, error: `No se pudo registrar la recomendación: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Registra un Outcome real enlazado a su recomendación o decisión de
   * origen. **Rechaza** con error todo Outcome cuyo origen no enlace a una
   * recomendación o decisión existente (R10.6); preserva el enlace
   * rec·dec·outcome (R10.3, R10.5).
   */
  async recordOutcome(o: Outcome, origen_ref: EntidadRef): Promise<Result<void>> {
    try {
      if (!o || typeof o.outcome_id !== "string" || o.outcome_id.length === 0) {
        return { ok: false, error: "El Outcome carece de un outcome_id válido.", degraded: false };
      }
      // El enlace de origen es el del parámetro o, en su defecto, el del propio Outcome.
      const origen = origen_ref ?? o.origen_ref;
      if (!origen || typeof origen.id !== "string" || origen.id.length === 0) {
        return {
          ok: false,
          error: "El Outcome carece de una recomendación o decisión de origen enlazada (R10.6).",
          degraded: false,
        };
      }
      await this.asegurar();
      const existeOrigen = this.recomendaciones.has(origen.id) || this.decisiones.has(origen.id);
      if (!existeOrigen) {
        return {
          ok: false,
          error: `El Outcome '${o.outcome_id}' carece de una recomendación o decisión de origen enlazada: no existe origen '${origen.id}' (R10.6).`,
          degraded: false,
        };
      }
      const store = await this.asegurar();
      const persistido: OutcomePersistido = { outcome: { ...o, origen_ref: origen }, origen_ref: origen };
      this.outcomes.set(o.outcome_id, persistido);
      await store.put<OutcomePersistido>("outcomes", { id: o.outcome_id, value: persistido });
      return { ok: true, value: undefined };
    } catch (err) {
      return { ok: false, error: `No se pudo registrar el Outcome: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Calcula de forma determinista las tasas de aceptación y de acierto en
   * `[0, 1]` para `(entidad, tipo)` a partir de las colecciones
   * materializadas (R10.5).
   */
  async rates(entidad: EntidadRef, tipo: string): Promise<Result<TasasMemoria>> {
    try {
      await this.asegurar();
      const recs = [...this.recomendaciones.values()];
      const decs = [...this.decisiones.values()];
      const outs = [...this.outcomes.values()];
      const tasas = computarTasas(recs, decs, outs, entidad, tipo ?? "");
      return { ok: true, value: tasas };
    } catch (err) {
      return { ok: false, error: `No se pudieron computar las tasas: ${mensajeError(err)}`, degraded: true };
    }
  }

  // -------------------------------------------------------------------------
  // Lectura de enlaces rec·dec·outcome para la AI_Observability (R14.5, R20.4)
  // -------------------------------------------------------------------------

  /**
   * Devuelve los enlaces recomendación·decisión·outcome materializados,
   * opcionalmente acotados a `ventana` por el `ts` de cada registro. Es la
   * capacidad de lectura mínima que la AI_Observability (tarea 10.2)
   * consume para derivar de forma determinista la AI acceptance rate, la
   * tasa de falsos positivos y la tasa de reversión (R14.5, R20.4). No
   * altera la interfaz {@link Memoria} del contrato.
   */
  async enlaces(ventana?: Ventana): Promise<Result<EnlacesMemoria>> {
    try {
      await this.asegurar();
      const recomendaciones = [...this.recomendaciones.values()]
        .filter((r) => tsEnVentana(r.rec.ts, ventana))
        .map((r) => ({ rec: r.rec, estado: r.estado }));
      const decisiones = [...this.decisiones.values()].filter((d) => tsEnVentana(d.ts, ventana));
      const outcomes = [...this.outcomes.values()]
        .filter((o) => tsEnVentana(o.outcome.ts, ventana))
        .map((o) => ({ outcome: o.outcome, origen_ref: o.origen_ref }));
      return { ok: true, value: { recomendaciones, decisiones, outcomes } };
    } catch (err) {
      return { ok: false, error: `No se pudieron leer los enlaces de la Memoria: ${mensajeError(err)}`, degraded: true };
    }
  }

  // -------------------------------------------------------------------------
  // Utilidades de diagnóstico/test
  // -------------------------------------------------------------------------

  /** Conteos de cada colección (uso de diagnóstico/test). */
  async size(): Promise<{ decisiones: number; recomendaciones: number; outcomes: number }> {
    await this.asegurar();
    return {
      decisiones: this.decisiones.size,
      recomendaciones: this.recomendaciones.size,
      outcomes: this.outcomes.size,
    };
  }
}
